import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalculatorIcon,
  ScaleIcon,
  FireIcon,
  HeartIcon,
  ChartBarIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  BookmarkIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { useAuthStore } from '../store/authStore';

type UnitSystem = 'metric' | 'imperial';
type Gender = 'male' | 'female';
type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
type TimeInterval = 'week' | 'month' | '3months' | '6months' | 'year' | 'all';

interface FormData {
  weight: string;
  height: string;
  age: string;
  gender: Gender;
  activityLevel: ActivityLevel;
  waist: string;
  neck: string;
  hip: string;
  restingHeartRate: string;
  unitSystem: UnitSystem;
}

interface MetricResult {
  metric: string;
  value: number;
  unit: string;
  category?: string;
  risk_level?: string;
  color?: string;
  interpretation?: string;
  healthy_range?: { min: number; max: number; unit: string };
  calorie_targets?: { weight_loss: number; maintenance: number; weight_gain: number };
  formulas?: Record<string, number | { min: number; max: number }>;
  zones?: Record<string, { name: string; min: number; max: number; description: string }>;
  max_heart_rate?: number;
  resting_heart_rate?: number | null;
}

interface AllMetricsResult {
  metrics: Record<string, MetricResult>;
  unit_system: string;
}

interface BMIRecord {
  id: number;
  weight: number;
  height: number;
  unit_system: string;
  bmi_value: number;
  bmi_category: string;
  notes: string | null;
  recorded_at: string;
  created_at: string;
}

interface BMIHistoryResponse {
  records: BMIRecord[];
  total_count: number;
  start_date: string | null;
  end_date: string;
  interval: string;
}

const ACTIVITY_LEVELS: { value: ActivityLevel; labelKey: string }[] = [
  { value: 'sedentary', labelKey: 'healthMetrics:activityLevels.sedentary' },
  { value: 'light', labelKey: 'healthMetrics:activityLevels.light' },
  { value: 'moderate', labelKey: 'healthMetrics:activityLevels.moderate' },
  { value: 'active', labelKey: 'healthMetrics:activityLevels.active' },
  { value: 'very_active', labelKey: 'healthMetrics:activityLevels.veryActive' },
];

const TIME_INTERVALS: { value: TimeInterval; labelKey: string }[] = [
  { value: 'week', labelKey: 'healthMetrics:intervals.week' },
  { value: 'month', labelKey: 'healthMetrics:intervals.month' },
  { value: '3months', labelKey: 'healthMetrics:intervals.threeMonths' },
  { value: '6months', labelKey: 'healthMetrics:intervals.sixMonths' },
  { value: 'year', labelKey: 'healthMetrics:intervals.year' },
  { value: 'all', labelKey: 'healthMetrics:intervals.allTime' },
];

export const HealthMetrics: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [results, setResults] = useState<AllMetricsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [bmiHistory, setBmiHistory] = useState<BMIRecord[]>([]);
  const [selectedInterval, setSelectedInterval] = useState<TimeInterval>('month');
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    weight: '',
    height: '',
    age: '',
    gender: 'male',
    activityLevel: 'sedentary',
    waist: '',
    neck: '',
    hip: '',
    restingHeartRate: '',
    unitSystem: 'imperial',
  });

  // Load BMI history when authenticated and interval changes
  useEffect(() => {
    if (isAuthenticated && showHistory) {
      loadBMIHistory();
    }
  }, [isAuthenticated, selectedInterval, showHistory]);

  const loadBMIHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await apiClient.get<BMIHistoryResponse>('/health-metrics/bmi/history', {
        params: {
          interval: selectedInterval,
          unit_system: formData.unitSystem
        }
      });
      setBmiHistory(response.data.records);
    } catch (err) {
      console.error('Failed to load BMI history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSaveSuccess(null);
  };

  const handleCalculate = async () => {
    // Validate required fields
    if (!formData.weight || !formData.height || !formData.age) {
      setError(t('healthMetrics:errors.requiredFields'));
      return;
    }

    setIsCalculating(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        weight: parseFloat(formData.weight),
        height: parseFloat(formData.height),
        age: parseInt(formData.age),
        gender: formData.gender,
        activity_level: formData.activityLevel,
        waist: formData.waist ? parseFloat(formData.waist) : null,
        neck: formData.neck ? parseFloat(formData.neck) : null,
        hip: formData.hip ? parseFloat(formData.hip) : null,
        resting_heart_rate: formData.restingHeartRate ? parseInt(formData.restingHeartRate) : null,
        unit_system: formData.unitSystem,
      };

      const response = await apiClient.post('/health-metrics/calculate-all', payload, {
        params: { language: i18n.language }
      });
      setResults(response.data);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('healthMetrics:errors.calculationFailed'));
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSaveBMI = async () => {
    if (!results?.metrics.bmi || !isAuthenticated) return;

    setIsSaving(true);
    setSaveSuccess(null);
    setError(null);

    try {
      await apiClient.post('/health-metrics/bmi/save', {
        weight: parseFloat(formData.weight),
        height: parseFloat(formData.height),
        bmi_value: results.metrics.bmi.value,
        bmi_category: results.metrics.bmi.category,
        unit_system: formData.unitSystem,
      });

      setSaveSuccess(t('healthMetrics:saveSuccess', 'BMI saved to your history!'));

      // Refresh history if it's open
      if (showHistory) {
        loadBMIHistory();
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('healthMetrics:errors.saveFailed', 'Failed to save BMI'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRecord = async (recordId: number) => {
    try {
      await apiClient.delete(`/health-metrics/bmi/history/${recordId}`);
      setBmiHistory(prev => prev.filter(r => r.id !== recordId));
    } catch (err) {
      console.error('Failed to delete record:', err);
    }
  };

  const handleReset = () => {
    setFormData({
      weight: '',
      height: '',
      age: '',
      gender: 'male',
      activityLevel: 'sedentary',
      waist: '',
      neck: '',
      hip: '',
      restingHeartRate: '',
      unitSystem: 'imperial',
    });
    setResults(null);
    setError(null);
    setSaveSuccess(null);
  };

  const getRiskColor = (color?: string) => {
    switch (color) {
      case 'green': return 'text-green-600 dark:text-green-400';
      case 'orange': return 'text-orange-500 dark:text-orange-400';
      case 'red': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getBgColor = (color?: string) => {
    switch (color) {
      case 'green': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'orange': return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
      case 'red': return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default: return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    }
  };

  const getCategoryColor = (category: string) => {
    if (category === 'normal') return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
    if (category === 'overweight' || category === 'underweight') return 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100';
    return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
    });
  };

  const handleClearAllHistory = async () => {
    if (!window.confirm(t('healthMetrics:confirmClearAll', 'Are you sure you want to delete all BMI history records? This cannot be undone.'))) {
      return;
    }

    try {
      // Delete all records one by one
      for (const record of bmiHistory) {
        await apiClient.delete(`/health-metrics/bmi/history/${record.id}`);
      }
      setBmiHistory([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <CalculatorIcon className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('healthMetrics:title', 'Health Metrics Calculator')}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          {t('healthMetrics:subtitle', 'Calculate BMI, BMR, TDEE, body fat percentage, and other health metrics')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('healthMetrics:enterYourInfo', 'Enter Your Information')}
          </h2>

          {/* Unit System Toggle */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('healthMetrics:unitSystem', 'Unit System')}
            </label>
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                type="button"
                onClick={() => handleInputChange('unitSystem', 'imperial')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                  formData.unitSystem === 'imperial'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('healthMetrics:imperial', 'Imperial (lbs/in)')}
              </button>
              <button
                type="button"
                onClick={() => handleInputChange('unitSystem', 'metric')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                  formData.unitSystem === 'metric'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('healthMetrics:metric', 'Metric (kg/cm)')}
              </button>
            </div>
          </div>

          {/* Required Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('healthMetrics:weight', 'Weight')} ({formData.unitSystem === 'imperial' ? 'lbs' : 'kg'}) *
                </label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder={formData.unitSystem === 'imperial' ? '150' : '68'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('healthMetrics:height', 'Height')} ({formData.unitSystem === 'imperial' ? 'in' : 'cm'}) *
                </label>
                <input
                  type="number"
                  value={formData.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder={formData.unitSystem === 'imperial' ? '68' : '173'}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('healthMetrics:age', 'Age')} *
                </label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('healthMetrics:gender', 'Gender')} *
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="male">{t('healthMetrics:male', 'Male')}</option>
                  <option value="female">{t('healthMetrics:female', 'Female')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('healthMetrics:activityLevel', 'Activity Level')}
              </label>
              <select
                value={formData.activityLevel}
                onChange={(e) => handleInputChange('activityLevel', e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
              >
                {ACTIVITY_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {t(level.labelKey, level.value)}
                  </option>
                ))}
              </select>
            </div>

            {/* Optional Fields */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {t('healthMetrics:optionalMeasurements', 'Optional measurements for additional metrics')}
              </p>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('healthMetrics:waist', 'Waist')}
                  </label>
                  <input
                    type="number"
                    value={formData.waist}
                    onChange={(e) => handleInputChange('waist', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder={formData.unitSystem === 'imperial' ? 'in' : 'cm'}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('healthMetrics:neck', 'Neck')}
                  </label>
                  <input
                    type="number"
                    value={formData.neck}
                    onChange={(e) => handleInputChange('neck', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder={formData.unitSystem === 'imperial' ? 'in' : 'cm'}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('healthMetrics:hip', 'Hip')}
                  </label>
                  <input
                    type="number"
                    value={formData.hip}
                    onChange={(e) => handleInputChange('hip', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                    placeholder={formData.unitSystem === 'imperial' ? 'in' : 'cm'}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('healthMetrics:restingHeartRate', 'Resting Heart Rate (bpm)')}
                </label>
                <input
                  type="number"
                  value={formData.restingHeartRate}
                  onChange={(e) => handleInputChange('restingHeartRate', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="60-80"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {saveSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-sm text-green-600 dark:text-green-400">{saveSuccess}</p>
              </div>
            )}

            <div className="flex space-x-3 pt-2">
              <button
                onClick={handleCalculate}
                disabled={isCalculating}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isCalculating ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    <span>{t('healthMetrics:calculating', 'Calculating...')}</span>
                  </>
                ) : (
                  <>
                    <CalculatorIcon className="h-5 w-5" />
                    <span>{t('healthMetrics:calculate', 'Calculate')}</span>
                  </>
                )}
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('healthMetrics:reset', 'Reset')}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {!results ? (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">
                {t('healthMetrics:enterInfoToCalculate', 'Enter your information and click Calculate to see your health metrics')}
              </p>
            </div>
          ) : (
            <>
              {/* BMI Result */}
              {results.metrics.bmi && (
                <div className={`rounded-xl border p-4 ${getBgColor(results.metrics.bmi.color)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-white dark:bg-gray-800 p-2 rounded-lg">
                        <ScaleIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {t('healthMetrics:results.bmi', 'Body Mass Index (BMI)')}
                        </h3>
                        <p className={`text-2xl font-bold ${getRiskColor(results.metrics.bmi.color)}`}>
                          {results.metrics.bmi.value} {results.metrics.bmi.unit}
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${
                      results.metrics.bmi.color === 'green' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                      results.metrics.bmi.color === 'orange' ? 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100' :
                      'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                    }`}>
                      {t(`healthMetrics:categories.${results.metrics.bmi.category}`, results.metrics.bmi.category?.replace('_', ' '))}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {results.metrics.bmi.interpretation}
                  </p>
                  {results.metrics.bmi.healthy_range && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {t('healthMetrics:healthyRange', 'Healthy range')}: {results.metrics.bmi.healthy_range.min} - {results.metrics.bmi.healthy_range.max} {results.metrics.bmi.healthy_range.unit}
                    </p>
                  )}
                  {/* Save BMI Button */}
                  {isAuthenticated && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={handleSaveBMI}
                        disabled={isSaving}
                        className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {isSaving ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            <span>{t('healthMetrics:saving', 'Saving...')}</span>
                          </>
                        ) : (
                          <>
                            <BookmarkIcon className="h-4 w-4" />
                            <span>{t('healthMetrics:saveBMI', 'Save BMI to history')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* BMR & TDEE */}
              {results.metrics.bmr && results.metrics.tdee && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-lg">
                      <FireIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('healthMetrics:results.calorieNeeds', 'Daily Calorie Needs')}
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('healthMetrics:results.bmr', 'Basal Metabolic Rate')}
                      </p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {results.metrics.bmr.value} <span className="text-sm font-normal">cal/day</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('healthMetrics:results.tdee', 'Total Daily Expenditure')}
                      </p>
                      <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                        {results.metrics.tdee.value} <span className="text-sm font-normal">cal/day</span>
                      </p>
                    </div>
                  </div>
                  {results.metrics.tdee.calorie_targets && (
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        {t('healthMetrics:calorieTargets', 'Calorie targets')}:
                      </p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthMetrics:weightLoss', 'Weight Loss')}</p>
                          <p className="font-semibold text-blue-600 dark:text-blue-400">{results.metrics.tdee.calorie_targets.weight_loss}</p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthMetrics:maintain', 'Maintain')}</p>
                          <p className="font-semibold text-green-600 dark:text-green-400">{results.metrics.tdee.calorie_targets.maintenance}</p>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthMetrics:weightGain', 'Weight Gain')}</p>
                          <p className="font-semibold text-orange-600 dark:text-orange-400">{results.metrics.tdee.calorie_targets.weight_gain}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Ideal Weight */}
              {results.metrics.ideal_weight && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                      <ChartBarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {t('healthMetrics:results.idealWeight', 'Ideal Body Weight')}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                    {(() => {
                      const bmiRange = results.metrics.ideal_weight.formulas?.bmi_range;
                      if (bmiRange && typeof bmiRange === 'object' && 'min' in bmiRange) {
                        return `${bmiRange.min} - ${bmiRange.max}`;
                      }
                      return '';
                    })()} {results.metrics.ideal_weight.unit}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {results.metrics.ideal_weight.interpretation}
                  </p>
                </div>
              )}

              {/* Body Fat */}
              {results.metrics.body_fat && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t('healthMetrics:results.bodyFat', 'Body Fat Percentage')}
                      </h3>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {results.metrics.body_fat.value}%
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t(`healthMetrics:bodyFatCategories.${results.metrics.body_fat.category}`, results.metrics.body_fat.category)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Waist-Hip Ratio */}
              {results.metrics.waist_hip_ratio && (
                <div className={`rounded-xl border p-4 ${getBgColor(results.metrics.waist_hip_ratio.color)}`}>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('healthMetrics:results.waistHipRatio', 'Waist-to-Hip Ratio')}
                  </h3>
                  <p className={`text-2xl font-bold ${getRiskColor(results.metrics.waist_hip_ratio.color)}`}>
                    {results.metrics.waist_hip_ratio.value}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {results.metrics.waist_hip_ratio.interpretation}
                  </p>
                </div>
              )}

              {/* Heart Rate Zones */}
              {results.metrics.heart_rate_zones && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
                      <HeartIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t('healthMetrics:results.heartRateZones', 'Heart Rate Training Zones')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('healthMetrics:maxHR', 'Max HR')}: {results.metrics.heart_rate_zones.max_heart_rate} bpm
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {results.metrics.heart_rate_zones.zones && Object.entries(results.metrics.heart_rate_zones.zones).map(([key, zone]) => (
                      <div key={key} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-white">{zone.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{zone.description}</span>
                        </div>
                        <span className="text-sm font-mono text-emerald-600 dark:text-emerald-400">
                          {zone.min}-{zone.max} bpm
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* BMI History Section */}
          {isAuthenticated && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100 dark:bg-indigo-900/30 p-2 rounded-lg">
                    <ChartBarIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('healthMetrics:bmiHistory', 'BMI History')}
                  </h3>
                </div>
                <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
              </button>

              {showHistory && (
                <div className="mt-4">
                  {/* Time interval selector */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {TIME_INTERVALS.map((interval) => (
                      <button
                        key={interval.value}
                        onClick={() => setSelectedInterval(interval.value)}
                        className={`px-3 py-1 text-sm rounded-full transition-colors ${
                          selectedInterval === interval.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {t(interval.labelKey, interval.value)}
                      </button>
                    ))}
                  </div>

                  {isLoadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <ArrowPathIcon className="h-6 w-6 animate-spin text-indigo-600" />
                    </div>
                  ) : bmiHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <BookmarkIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>{t('healthMetrics:noHistory', 'No BMI records yet. Calculate and save your BMI to track your progress!')}</p>
                    </div>
                  ) : (
                    <>
                      {/* BMI Chart - Simple visual representation */}
                      <div className="mb-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                        <div className="flex">
                          {/* Y-axis label */}
                          <div className="flex flex-col items-center justify-center mr-2">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 transform -rotate-90 whitespace-nowrap">
                              {t('healthMetrics:yAxisBMI', 'BMI')}
                            </span>
                          </div>
                          {/* Y-axis values */}
                          <div className="flex flex-col justify-between text-xs text-gray-600 dark:text-gray-300 pr-2 h-40">
                            <span>40</span>
                            <span>30</span>
                            <span>25</span>
                            <span>18.5</span>
                            <span>10</span>
                          </div>
                          {/* Chart area */}
                          <div className="flex-1 overflow-x-auto">
                            <div className="relative h-40 min-w-0">
                              {/* BMI zones background */}
                              <div className="absolute inset-x-0 top-0 h-full flex flex-col">
                                <div className="flex-1 bg-red-100 dark:bg-red-900/30" style={{ flex: '33.3%' }} title="Obese (>30)" />
                                <div className="flex-1 bg-orange-100 dark:bg-orange-900/30" style={{ flex: '16.7%' }} title="Overweight (25-30)" />
                                <div className="flex-1 bg-green-100 dark:bg-green-900/30" style={{ flex: '21.7%' }} title="Normal (18.5-25)" />
                                <div className="flex-1 bg-orange-100 dark:bg-orange-900/30" style={{ flex: '28.3%' }} title="Underweight (<18.5)" />
                              </div>
                              {/* Chart points */}
                              <div className="relative h-full flex items-end justify-around">
                                {bmiHistory.slice(-12).map((record) => {
                                  // Map BMI value to height percentage (assuming BMI range 10-40)
                                  const heightPercent = Math.min(100, Math.max(0, ((record.bmi_value - 10) / 30) * 100));
                                  return (
                                    <div
                                      key={record.id}
                                      className="flex flex-col items-center group"
                                      style={{ height: `${heightPercent}%` }}
                                    >
                                      <div className="relative">
                                        <div
                                          className={`w-3 h-3 rounded-full ${
                                            record.bmi_category === 'normal' ? 'bg-green-500' :
                                            record.bmi_category === 'overweight' || record.bmi_category === 'underweight' ? 'bg-orange-500' :
                                            'bg-red-500'
                                          }`}
                                        />
                                        {/* Tooltip */}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                            <p className="font-semibold">{record.bmi_value}</p>
                                            <p>{formatDate(record.recorded_at)}</p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            {/* X-axis dates */}
                            <div className="flex justify-around mt-1 border-t border-gray-200 dark:border-gray-600 pt-1">
                              {bmiHistory.slice(-12).map((record) => (
                                <span key={record.id} className="text-[10px] text-gray-600 dark:text-gray-300 transform -rotate-45 origin-top-left whitespace-nowrap">
                                  {formatShortDate(record.recorded_at)}
                                </span>
                              ))}
                            </div>
                            {/* X-axis label */}
                            <div className="text-center mt-4">
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                {t('healthMetrics:xAxisDate', 'Date')}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Legend */}
                        <div className="flex justify-center gap-4 mt-3 text-xs text-gray-700 dark:text-gray-300">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            {t('healthMetrics:categories.normal', 'Normal')}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                            {t('healthMetrics:overUnder', 'Over/Under')}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            {t('healthMetrics:categories.obese_class_1', 'Obese')}
                          </span>
                        </div>
                      </div>

                      {/* History list */}
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {bmiHistory.slice().reverse().map((record) => (
                          <div
                            key={record.id}
                            className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getCategoryColor(record.bmi_category)}`}>
                                {record.bmi_value}
                              </span>
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {record.weight} {record.unit_system === 'imperial' ? 'lbs' : 'kg'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {formatDate(record.recorded_at)}
                              </span>
                              <button
                                onClick={() => handleDeleteRecord(record.id)}
                                className="p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                                title={t('healthMetrics:deleteRecord', 'Delete record')}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Clear All Button */}
                      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <button
                          onClick={handleClearAllHistory}
                          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span>{t('healthMetrics:clearAllHistory', 'Clear All History')}</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Disclaimer */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {t('healthMetrics:disclaimer', 'These calculations are estimates for educational purposes only. Consult a healthcare provider for personalized medical advice.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
